import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { VirtualScrolling } from '@cube3/ui/src/VirtualPages/next/VirtualScrolling';
import { ExportQueueListItem } from './prefabs/ExportQueueListItem';
import { Typography } from '@cube3/ui/src/typography/Typography';
import { Padding } from '../Contracts/prefabs/Padding';

// 60 day lookback
const CREATED_AFTER = new Date(
  Date.now() - 64 * 24 * 60 * 60 * 1000
).toISOString();

export const ExportQueue = () => {
  const [workspaceId] = useCurrentWorkspace();
  const [accountId] = useCurrentAccount();

  // for virtual scrolling
  const [pageSize, setPageSize] = useState(9);
  const [focusItem, setFocusItem] = useState(0);

  const params = useMemo(() => {
    return {
      filter: {
        account: accountId,
        created_after: CREATED_AFTER
      },
      page: {
        size: pageSize
      }
    };
  }, [accountId, pageSize]);

  const focusPage = pageSize ? Math.floor(focusItem / pageSize) + 1 : 1;

  const pages = useMemo(() => {
    return [focusPage - 1, focusPage, focusPage + 1].filter((p) => p > 0);
  }, [focusPage]);

  const {
    resources: batches,
    pageCounts: { itemCount },
    retrieve
  } = useResourceList__ALPHA({
    resourceType: 'workspace',
    resourceId: pageSize ? workspaceId : undefined,
    edgeType: 'export-batch',
    edgeLabel: 'export-batches',
    params,
    pages
  });

  const items = useMemo(() => {
    const set = batches; //.slice(low, high);
    const low = (pages[0] - 1) * pageSize;
    const items = set.map((i, idx) => ({ ...i, pos: low + idx }));
    return items;
  }, [batches, pages, pageSize]);

  const inprogress = useMemo(() => {
    return batches?.filter(
      (res) =>
        res.attributes.state !== 'success' && res.attributes.state !== 'failed'
    );
  }, [batches]);

  // make sure we clear all the pages
  useEffect(() => {
    retrieve();
  }, [retrieve]);

  // poll batches
  useEffect(() => {
    if (inprogress && inprogress.length > 0) {
      const timer = setTimeout(() => retrieve(), 3000);
      return () => window.clearTimeout(timer);
    }
  }, [retrieve, inprogress]);

  const count = items?.length || 0;
  const inferedTotal = count % pageSize === 0 ? count + 1 : count;

  return count > 0 ? (
    <VirtualScrolling
      height={'100%'}
      items={items}
      itemHeight={210}
      totalItems={itemCount || inferedTotal}
      focusItem={focusItem}
      onFocusItem={setFocusItem}
      onPageSize={setPageSize}
      pageSize={pageSize}
      itemComponent={RenderItem}
    />
  ) : (
    <Padding>
      <Typography color="contrast2" typographyProps={{ gutterBottom: true }}>
        No recent exports
      </Typography>
      <Typography
        color="contrast3"
        variant="body3"
        typographyProps={{ gutterBottom: true }}
      >
        No exports have taken place in the past 2 months
      </Typography>
    </Padding>
  );
};

const RenderItem = ({ item }) => {
  return <ExportQueueListItem exportBatch={item} />;
};
